/**
 * ChatModal - Chat desktop-mobile con WebSocket real-time
 * Fallback automatico a polling se WebSocket fallisce
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Wifi, WifiOff, Maximize2, Minimize2, Trash2, MoreVertical } from 'lucide-react';
import { ConfirmModal } from '../../../components/ConfirmModal';

interface ChatMessage {
  id?: number;
  from: 'mobile' | 'desktop';
  text: string;
  time: string;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  autoOpenChat: boolean;
  setAutoOpenChat: (value: boolean) => void;
}

const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  autoOpenChat,
  setAutoOpenChat
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [lastChatId, setLastChatId] = useState<number>(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [usePolling, setUsePolling] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll su nuovo messaggio
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Polling fallback
  const startPollingFallback = () => {
    console.log('[ChatModal] ⚠️ WebSocket fallito, uso polling fallback');
    setUsePolling(true);

    const pollMessages = async () => {
      try {
        const url = `${window.location.origin}/api/chat/messages/${sessionId}${lastChatId > 0 ? `?since=${lastChatId}` : ''}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id).filter(id => id !== undefined));
              const newMessages = data.messages.filter((m: any) => !existingIds.has(m.id));
              return [...prev, ...newMessages];
            });

            const maxId = Math.max(...data.messages.map((m: any) => m.id));
            setLastChatId(maxId);
          }
        }
      } catch (error) {
        console.error('[ChatModal] Errore polling:', error);
      }
    };

    pollMessages();
    pollingIntervalRef.current = setInterval(pollMessages, 2000);
  };

  // Fetch iniziale messaggi quando modale si apre
  useEffect(() => {
    if (!isOpen) return;

    const fetchInitialMessages = async () => {
      try {
        console.log('[ChatModal] 📥 Carico messaggi esistenti...');
        const response = await fetch(`${window.location.origin}/api/chat/messages/${sessionId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.messages && data.messages.length > 0) {
            setMessages(data.messages);
            const maxId = Math.max(...data.messages.map((m: any) => m.id));
            setLastChatId(maxId);
            console.log('[ChatModal] ✅ Caricati', data.messages.length, 'messaggi');
          }
        }
      } catch (error) {
        console.error('[ChatModal] ❌ Errore caricamento messaggi:', error);
      }
    };

    fetchInitialMessages();
  }, [isOpen, sessionId]);

  // Setup WebSocket quando modale si apre
  useEffect(() => {
    if (!isOpen) return;

    console.log('[ChatModal] 🔌 Connessione WebSocket...');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/chat`);
    wsRef.current = ws;

    let authTimeout: NodeJS.Timeout;

    ws.onopen = () => {
      console.log('[ChatModal] ✅ WebSocket connesso');
      setWsConnected(true);

      // Autentica
      ws.send(JSON.stringify({
        type: 'auth',
        sessionId,
        clientType: 'desktop'
      }));

      // Se autenticazione non arriva entro 5s, fallback a polling
      authTimeout = setTimeout(() => {
        if (!wsConnected) {
          console.warn('[ChatModal] ⚠️ Timeout autenticazione, uso polling');
          ws.close();
          startPollingFallback();
        }
      }, 5000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'auth_success':
            clearTimeout(authTimeout);
            console.log('[ChatModal] ✅ Autenticazione WebSocket riuscita');
            break;

          case 'chat_message':
            console.log('[ChatModal] 💬 Messaggio ricevuto via WebSocket:', data.message);
            // Aggiungi messaggio se non già presente
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id).filter(id => id !== undefined));
              if (data.message.id && existingIds.has(data.message.id)) {
                return prev; // Già presente
              }
              return [...prev, data.message];
            });

            if (data.message.id) {
              setLastChatId(prev => Math.max(prev, data.message.id));
            }
            break;

          case 'clear_chat':
            console.log('[ChatModal] 🗑️ Ricevuto clear_chat, cancello messaggi locali');
            setMessages([]);
            setLastChatId(0);
            break;

          case 'pong':
            break;

          default:
            console.log('[ChatModal] Messaggio WebSocket:', data.type);
        }
      } catch (error) {
        console.error('[ChatModal] Errore parsing WebSocket:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[ChatModal] ❌ Errore WebSocket:', error);
    };

    ws.onclose = () => {
      console.log('[ChatModal] 🔌 WebSocket disconnesso');
      setWsConnected(false);

      // Fallback a polling solo se modale ancora aperta
      if (isOpen && !usePolling) {
        startPollingFallback();
      }
    };

    return () => {
      console.log('[ChatModal] Cleanup WebSocket');
      clearTimeout(authTimeout);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      ws.close();
    };
  }, [isOpen, sessionId]);

  // Invia messaggio (usa WebSocket se disponibile, altrimenti HTTP)
  const sendMessage = async () => {
    if (!input.trim()) return;

    const messageText = input.trim();
    setInput('');

    if (wsConnected && wsRef.current?.readyState === WebSocket.OPEN) {
      // Usa WebSocket
      console.log('[ChatModal] 📤 Invio via WebSocket');
      wsRef.current.send(JSON.stringify({
        type: 'chat_message',
        from: 'desktop',
        text: messageText
      }));
    } else {
      // Fallback HTTP
      console.log('[ChatModal] 📤 Invio via HTTP (fallback)');
      try {
        const response = await fetch(`${window.location.origin}/api/chat/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            fromType: 'desktop',
            message: messageText
          })
        });

        if (response.ok && usePolling) {
          // Forza polling immediato
          const url = `${window.location.origin}/api/chat/messages/${sessionId}${lastChatId > 0 ? `?since=${lastChatId}` : ''}`;
          const pollResponse = await fetch(url);

          if (pollResponse.ok) {
            const data = await pollResponse.json();
            if (data.messages && data.messages.length > 0) {
              setMessages(prev => {
                const existingIds = new Set(prev.map(m => m.id).filter(id => id !== undefined));
                const newMessages = data.messages.filter((m: any) => !existingIds.has(m.id));
                return [...prev, ...newMessages];
              });

              const maxId = Math.max(...data.messages.map((m: any) => m.id));
              setLastChatId(maxId);
            }
          }
        }
      } catch (error) {
        console.error('[ChatModal] Errore invio:', error);
      }
    }
  };

  // Cancella messaggi
  const clearMessages = async (deleteForBoth: boolean) => {
    try {
      if (deleteForBoth) {
        // Cancella per entrambi: chiama API backend per cancellare DB e fare broadcast
        const response = await fetch(`${window.location.origin}/api/chat/clear/${sessionId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          console.log('[ChatModal] ✅ Messaggi cancellati per entrambi');
          setMessages([]);
          setLastChatId(0);
        } else {
          console.error('[ChatModal] ❌ Errore cancellazione:', response.status);
        }
      } else {
        // Cancella solo per me: cancella solo stato locale senza toccare DB
        console.log('[ChatModal] 🗑️ Cancello messaggi solo localmente');
        setMessages([]);
        setLastChatId(0);
      }
    } catch (error) {
      console.error('[ChatModal] ❌ Errore clearMessages:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-md h-[600px] flex flex-col border border-gray-700">
        {/* Header */}
        <div className="border-b border-gray-700">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">Chat con Telefono</h3>
              {wsConnected ? (
                <div className="flex items-center gap-1 text-green-400 text-xs">
                  <Wifi className="w-3 h-3" />
                  <span>Real-time</span>
                </div>
              ) : usePolling ? (
                <div className="flex items-center gap-1 text-yellow-400 text-xs">
                  <WifiOff className="w-3 h-3" />
                  <span>Polling</span>
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="Menu"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-8 bg-gray-900 border border-gray-700 rounded-lg shadow-lg py-1 w-56 z-10">
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setConfirmModal({
                          isOpen: true,
                          title: 'Cancella messaggi',
                          message: 'Vuoi cancellare i messaggi solo sul tuo PC? Il telefono continuerà a visualizzarli.',
                          variant: 'warning',
                          onConfirm: () => clearMessages(false)
                        });
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Cancella messaggi (solo io)
                    </button>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        setConfirmModal({
                          isOpen: true,
                          title: 'Cancella per entrambi',
                          message: 'Vuoi cancellare i messaggi per entrambi i dispositivi (PC e telefono)? Questa azione non può essere annullata.',
                          variant: 'danger',
                          onConfirm: () => clearMessages(true)
                        });
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-800 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Cancella per entrambi
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 px-4 pb-3 text-xs">
            <button
              onClick={() => setAutoOpenChat(!autoOpenChat)}
              className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded transition-colors text-gray-300"
              title={autoOpenChat ? 'Disabilita apertura automatica' : 'Abilita apertura automatica'}
            >
              {autoOpenChat ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
              <span>{autoOpenChat ? 'Auto-apri' : 'Manuale'}</span>
            </button>
          </div>
        </div>

        {/* Messaggi */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center text-gray-400 mt-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nessun messaggio</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.from === 'desktop' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg p-3 ${
                    msg.from === 'desktop'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-100'
                  }`}
                >
                  <p className="text-sm">{msg.text}</p>
                  <p className={`text-xs mt-1 ${msg.from === 'desktop' ? 'text-blue-200' : 'text-gray-400'}`}>
                    {msg.time ? new Date(msg.time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Scrivi un messaggio..."
              className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal conferma */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Conferma"
        cancelText="Annulla"
      />
    </div>
  );
};

export default ChatModal;
